<script lang="ts">
	import { beforeUpdate, onMount } from 'svelte';
	import { push, replace, querystring } from 'svelte-spa-router';
	import { format } from '../services/i18n';
	import { parse } from 'qs';

	import { standardLogin, req_password } from '../services/http';

	import { Text, ButtonV2, InputV3, Icon, AlertV2, toast } from '@Shelly/shelly-design-system';
	import { capFirstChar, fillMissingUnitNames } from 'src/helpers/utilities';
	import { clearDataOnLogin, handleLoginErrors } from 'src/helpers/account_helpers';

	import sha1 from 'crypto-js/sha1';
	import { handleUserProfile } from 'src/helpers/login_helpers';

	import { validate_email } from 'src/helpers/validators';
	import { normalizeEmail } from 'src/helpers/email_helpers';
	import { testId } from 'src/helpers/test_helpers';
	import { isMultiUnitResult, nativeLogin } from 'src/services/auth_v2';
	import UnitSelectionModalV2 from '../components/UnitSelectionModalV2.svelte';
	import type {
		AvailableSubUnits,
		AvailableUnits,
		LoginResult,
		MultiUnitLoginResult,
	} from 'src/types/responses/usersAuthResponse';
	import { user_data } from 'src/stores/account/user';

	let host = import.meta.env.VITE_APP_BUCKET || window.location.host;

	// export let route = true;

	let email = '';
	let pass = '';
	let mail_error: boolean | string = false;
	let pass_error: boolean | string = false;
	let forgotten = false;
	let show_alert = false;
	let alertProps = {
		heading: '',
		message: '',
	};
	let pass_request_success = false;
	let login_loading = false;
	let page_title = '';
	let page_subtitle = '';

	let showUnitModal = false;
	let availableUnits: AvailableUnits = {};
	let availableSubUnits: AvailableSubUnits = {};
	let selectedUnitId: string | undefined;
	let storedHashPass = '';
	let storedEmail = '';
	let discoveryToken: string | undefined;
	let discoveryApiUrl: string | undefined;

	onMount(() => {
		if ($querystring && $querystring !== 'forgotten-password') {
			const parsed = parse($querystring);
			email = parsed.email;
			pass = parsed.pass;
			handle_login(email, pass);
		} else if ($querystring && $querystring === 'forgotten-password') {
			forgotten = true;
			replace('/login');
		}
	});

	beforeUpdate(() => {
		//The functions below are used to change title and subtitle of the page while user switches between login and forgotten password
		page_title = get_page_title();
		page_subtitle = get_page_subtitle();
	});

	// handle inputs verification before sending to the request function
	// validates email and password inputs
	const validateEmailAndPassword = () => {
		const isValidState = {
			email: true,
			password: true,
		};

		if (email === '') {
			mail_error = $format('label_enter_email', {
				default: 'Please enter an email!',
			});
			isValidState.email = false;
		} else if (!validate_email(email)) {
			mail_error = $format('input_error_valid_email', {
				default: 'Please enter a valid email!',
			});
			isValidState.email = false;
		}

		if (pass === '') {
			pass_error = $format('label_enter_password', {
				default: 'Please enter a password!',
			});
			isValidState.password = false;
		}
		return isValidState.email && isValidState.password;
	};

	// validates only email input ( for when forgotten password )
	const validateEmail = () => {
		const isValidState = {
			email: true,
		};
		if (email === '') {
			mail_error = $format('label_enter_email', {
				default: 'Please enter an email!',
			});
			isValidState.email = false;
		} else if (!validate_email(email)) {
			mail_error = $format('input_error_valid_email', {
				default: 'Please enter a valid email!',
			});
			isValidState.email = false;
		}
		return isValidState.email;
	};

	const handle_login = async (email, pass) => {
		email = normalizeEmail(email);

		if (!validateEmailAndPassword()) return;

		clear_errors();
		login_loading = true;

		let hashPass = pass;
		if (!$querystring) {
			hashPass = sha1(pass).toString();
		}

		storedHashPass = hashPass;
		storedEmail = email;

		let res: LoginResult | MultiUnitLoginResult = await standardLogin(
			email,
			hashPass,
			undefined,
			undefined,
		);

		if (isMultiUnitResult(res)) {
			availableUnits = fillMissingUnitNames(res.availableUnits);
			availableSubUnits = res.availableSubUnits ?? {};
			selectedUnitId = res.selectedUnitId?.toString();
			discoveryToken = res.data?.token;
			discoveryApiUrl = res.data?.user_api_url;
		}

		const unitIds = Object.keys(availableUnits);
		const subUnitIds = Object.keys(availableSubUnits);
		const totalChoices = unitIds.length + subUnitIds.length;

		if (res?.isok && totalChoices > 1) {
			const hasTokens = res.accessToken;

			// case for users with only 1 sub unit
			if (!hasTokens) {
				const firstSub = Object.entries(availableSubUnits)[0];

				if (!firstSub) {
					toast.open({ type: 'danger', title: $format('error_global') });
					login_loading = false;
					return;
				}

				res = await standardLogin(email, hashPass, firstSub[0], firstSub[1]?.sub);

				nativeLogin(
					email,
					res.data.password,
					res.data.user_api_url,
					res.data.token,
					res.data.lang,
				);
				clearDataOnLogin();

				await handleUserProfile(hashPass);
				login_loading = false;
				return;
			}

			showUnitModal = true;
			login_loading = false;
			return;
		}

		if (res?.isok && totalChoices === 1) {
			const hasTokens = res.accessToken;

			// case for users with only 1 sub unit
			if (!hasTokens) {
				const firstSub = Object.entries(availableSubUnits)[0];

				if (!firstSub) {
					toast.open({ type: 'danger', title: $format('error_global') });
					login_loading = false;
					return;
				}

				res = await standardLogin(email, hashPass, firstSub[0], firstSub[1]?.sub);

				nativeLogin(
					email,
					res.data.password,
					res.data.user_api_url,
					res.data.token,
					res.data.lang,
				);
				clearDataOnLogin();

				await handleUserProfile(hashPass);
				login_loading = false;
				return;
			}

			nativeLogin(email, res.data.password, res.data.user_api_url, res.data.token, res.data.lang);
			clearDataOnLogin();

			await handleUserProfile(hashPass);
			login_loading = false;
		} else {
			login_loading = false;

			alertProps = handleLoginErrors(res);

			if ($querystring) {
				pass = '';
				replace('/login');
			}

			show_alert = true;
		}
	};

	const onUnitSelected = async (event: CustomEvent<{ unitId: string; subId?: number }>) => {
		showUnitModal = false;
		login_loading = true;

		const { unitId, subId } = event.detail;
		const res = await standardLogin(
			storedEmail,
			storedHashPass,
			unitId,
			subId != null ? String(subId) : undefined,
		);

		if (res?.isok) {
			nativeLogin(
				storedEmail,
				res.data.password,
				res.data.user_api_url,
				res.data.token,
				res.data.lang,
			);
			clearDataOnLogin();
			await handleUserProfile(storedHashPass);
			login_loading = false;
		} else {
			login_loading = false;
			alertProps = handleLoginErrors(res);
			show_alert = true;
		}
	};

	const clear_errors = () => {
		if (show_alert) {
			show_alert = false;
			alertProps = {
				heading: '',
				message: '',
			};
		}
		if (mail_error) {
			mail_error = false;
		}
		if (pass_error) {
			pass_error = false;
		}
	};

	const toggle_forgotten = () => {
		forgotten = !forgotten;
		clear_errors();
	};

	const req_password_caller = async (email) => {
		if (validateEmail()) {
			login_loading = true;
			await req_password(email)
				.then((res) => {
					login_loading = false;

					if (res && !res.error) {
						pass_request_success = true;
					} else {
						if (res.error === 'not_existing') {
							alertProps.heading = $format('error_wrong_email', {
								default: `Error, ${res.error}`,
							});
							alertProps.message = $format('no_account_err_toast', {
								default: 'There is no account with this email.',
							});
						} else if (res.error === 'mail_sent') {
							alertProps.heading = $format('alert_mail_sent', {
								default: `Error, ${res.error}`,
							});
							alertProps.message = $format('alert_error_mail_sent', {
								default: 'Mail already sent in last hour.',
							});
						}
						pass_request_success = false;
						show_alert = true;
					}
				})
				.catch((e) => console.error(e));
		}
	};

	const back_to_login = () => {
		pass_request_success = false;
		toggle_forgotten();
	};

	const get_page_title = () => {
		if (!forgotten) {
			return $format('login');
		}

		if (forgotten && pass_request_success) {
			return $format('password_request_was_sent', {
				default: 'Password request was sent',
			});
		}

		if (forgotten) {
			return $format('reset_password', { default: 'Reset Password' });
		}
	};

	const get_page_subtitle = () => {
		if (pass_request_success) {
			return $format('please_check_your_email', {
				default: 'Please check your e-mail.',
			});
		}
		return $format('text_forgotten_pass_enter_email', {
			default: 'Enter the email you used for your account.',
		});
	};

	const onInputEmail = (event: Event) => {
		const target = event.target as HTMLInputElement;
		email = target.value;
	};

	const onInputPass = (event: Event) => {
		const target = event.target as HTMLInputElement;
		pass = target.value;
		target.blur(); //prevent ios resize on login
	};

	$: emailTestId = forgotten ? 'forgotten-email-input' : 'login-email-input';

	$: urlTC = `<a class="text-small link-styles hover-opacity" target="_blank" rel="noopener noreferrer" href="/">
						${$format('shelly_terms_and_conditions', {
								default: 'Shelly’s Terms and conditions',
							})}
					</a>`;
	$: urlPP = `<a class="text-small link-styles hover-opacity" target="_blank" rel="noopener noreferrer" href="/">
						${$format('privacy', { default: 'Privacy policy' })}
					</a>`;
	$: urlLTC = `<a class="text-small link-styles hover-opacity" target="_blank" rel="noopener noreferrer" href="/">
						${$format('legal_terms_and_conditions', {
								default: 'Legal Terms and conditions',
							})}
					</a>`;
	$: loginLinks = $format('login_links_for_pp_and_tc', {
		default: `By logging in, you agree to {urlTC} and {urlPP}. Please see our {urlLTC}.`,
		values: {
			urlTC,
			urlPP,
			urlLTC,
		},
	});
</script>

{#if pass_request_success && forgotten}
	<div class="d-flex justify-content-center mb-m">
		<Icon icon="fa-light fa-thumbs-up text-success" size="l" />
	</div>
{/if}

<Text element="h1" class={`${!forgotten ? 'mb-l' : 'mb-m'}  pl-s`}>
	{page_title}
</Text>

{#if forgotten}
	<Text class="mb-xl pl-s">
		{page_subtitle}
	</Text>
{/if}

{#if show_alert}
	<AlertV2 type="danger" heading={alertProps.heading} message={alertProps.message} class="mb-m" />
{/if}

{#if !pass_request_success}
	<InputV3
		id="input-main-email"
		aria-labelledby="input-main-email"
		data-testid={testId(emailTestId)}
		bind:value={email}
		size="l"
		class="mb-m"
		label={capFirstChar($format('placeholder_email', { default: 'e-mail' }))}
		icon="fa-regular fa-envelope"
		type="text"
		inputmode={'email'}
		error={mail_error ? `${mail_error}` : ''}
		on:click={() => clear_errors()}
		on:enter={() => handle_login(email, pass)}
		on:change={onInputEmail}
		disabled={login_loading}
	/>
{/if}

{#if !forgotten}
	<InputV3
		id="input-main-password"
		aria-labelledby="input-main-password"
		data-testid={testId('login-password-input')}
		bind:value={pass}
		size="l"
		class="mb-s"
		label={capFirstChar($format('placeholder_password', { default: 'password' }))}
		icon="fa-regular fa-lock-keyhole"
		type="password"
		error={pass_error ? `${pass_error}` : ''}
		on:click={() => clear_errors()}
		on:escape
		on:change={onInputPass}
		on:enter={() => handle_login(email, pass)}
		disabled={login_loading}
		a11yProps={{
			iconButtonAriaLabel: $format('button_show_password', {
				default: 'Show password',
			}),
		}}
	/>

	<Text
		element="span"
		class={`mb-m d-flex justify-content-end link-styles hover-opacity text-small ${login_loading ? ' disabled' : ''}`}
		on:click={() => {
			if (!login_loading) toggle_forgotten();
		}}
	>
		{$format('forgot_password', { default: 'Forgot password?' })}
	</Text>

	<ButtonV2
		width="full"
		class="mb-m"
		data-testid={testId('login-submit-button')}
		disabled={login_loading}
		loading={login_loading}
		on:click={() => handle_login(email, pass)}
	>
		{$format('login')}
	</ButtonV2>

	<!-- This approach will be used when PP, TC and LTC landing pages are ready -->
	<!-- <Text class="text-small">
		{@html loginLinks}
	</Text> -->

	<!-- This is workaround until PP, TC and LTC landing pages are ready -->
	<Text class="text-small">
		{$format('text_by_logging', {
			default: 'By logging in, you agree to',
		})}
		<span
			role="presentation"
			class="text-small link-styles hover-opacity"
			on:click={() => push(`/terms-and-conditions`)}
			>{$format('shelly_terms_and_conditions', {
				default: 'Shelly’s Terms and conditions',
			})}</span
		>{', '}
		<span
			role="presentation"
			class="text-small link-styles hover-opacity"
			on:click={() => push(`/privacy-policy`)}
			>{$format('privacy', { default: 'Privacy policy' })}</span
		>
		{$format('and', { default: 'and' })}
		<span
			role="presentation"
			class="text-small link-styles hover-opacity"
			on:click={() => push(`/data-act`)}>{$format('data_act', { default: 'Data Act' })}</span
		>.
	</Text>
	<Text class="mt-s text-small">
		{$format('change_log_intro', {
			default: 'Keep up with what’s new and improved in the',
		})}
		<span
			role="presentation"
			class="text-small link-styles hover-opacity"
			on:click={() => push(`/changelog`)}
		>
			{capFirstChar(
				$format('shelly_change_log', {
					default: 'Shelly Changelog',
				}),
			)}</span
		>.
	</Text>

	<hr class="mt-xl mb-xl" />

	<ButtonV2
		width="full"
		variant="secondary"
		on:click={() => {
			push('/signup');
		}}
		disabled={login_loading}
	>
		{$format('create_account')}
	</ButtonV2>
{:else}
	{#if !pass_request_success}
		<ButtonV2
			class="mb-m"
			width="full"
			loading={login_loading}
			disabled={email === '' || login_loading}
			on:click={() => req_password_caller(email)}
		>
			{$format('button_request_new_pass', { default: 'Request new password' })}
		</ButtonV2>
	{/if}

	<ButtonV2
		width="full"
		variant={pass_request_success ? 'primary' : 'secondary'}
		disabled={login_loading}
		on:click={back_to_login}
	>
		{pass_request_success
			? $format('link_back_to_login', { default: 'Back to login' })
			: $format('button_back', { default: 'Back' })}
	</ButtonV2>
{/if}

<UnitSelectionModalV2
	bind:showModal={showUnitModal}
	units={availableUnits}
	subUnits={availableSubUnits}
	{selectedUnitId}
	accountEmail={storedEmail}
	authToken={discoveryToken}
	userApiUrl={discoveryApiUrl}
	on:select={onUnitSelected}
/>

<style>
	:global(.link-styles.disabled) {
		color: var(--color-3);
		cursor: not-allowed;
	}
</style>
